import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { UknUser } from '@/store/auth';

/**
 * Auth-related mutations the user-menu uses. Login itself is in pages/Login
 * because it needs to set the token + user on success — fine to keep there.
 */

export type ChangePasswordPayload = {
  current_password: string;
  new_password: string;
};

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: ChangePasswordPayload) =>
      api.post<void>('/auth/change-password', body),
  });
}

/** POST /auth/switch-role: a token re-issued for the chosen role, on the same expiry, and the profile it gives. */
export type SwitchRoleResult = { access_token: string; token_type: string; user: UknUser };

/**
 * Act as one held role (or, with null, as all of them). The server checks the
 * role against everything the user holds and only ever narrows their rights;
 * the caller stores the returned session and reloads the app for it.
 */
export function useSwitchRole() {
  return useMutation({
    mutationFn: (role: string | null) =>
      api.post<SwitchRoleResult>('/auth/switch-role', { role }),
  });
}

/** One entry in the role switcher. `target` is what to send: a role key, or null for every held role. */
export type RoleChoice = { target: string | null; label: string; selected: boolean };

const BASELINE = 'EMPLOYEE';

/**
 * The switcher's entries for `user`, or [] when there is nothing to switch.
 *
 * Every role is layered on EMPLOYEE (self-service), so with a single other
 * role "all my roles" and that role are the same thing: that role's entry
 * sends null and no separate "all" entry is shown. With two or more other
 * roles an "All my roles" entry comes first.
 */
export function roleChoices(user: UknUser | null | undefined): RoleChoice[] {
  const roles = user?.roles ?? [];
  if (roles.length < 2) return [];
  const acting = user?.acting_role ?? null;
  const others = roles.filter((r) => r.key !== BASELINE);
  if (others.length >= 2) {
    return [
      { target: null, label: 'All my roles', selected: acting === null },
      ...roles.map((r) => ({ target: r.key, label: r.name, selected: acting === r.key })),
    ];
  }
  const only = others[0]?.key;
  return roles.map((r) => (r.key === only
    ? { target: null, label: r.name, selected: acting === null || acting === r.key }
    : { target: r.key, label: r.name, selected: acting === r.key }));
}

/** The name to show for the role label the profile reports (the acting role while switched); its key when unnamed. */
export function roleLabel(user: UknUser | null | undefined): string {
  const key = user?.role ?? '';
  return user?.roles?.find((r) => r.key === key)?.name ?? key;
}
